import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";

import refreshAuth from "../services/refreshAuth";

let lastGlobalRefreshTime = 0;
export const REFRESH_COOLDOWN_MS = 10000; // 10 seconds

export class RefreshCooldownError extends Error {
  constructor(remainingMs: number) {
    super("REFRESH_COOLDOWN");
    this.name = "RefreshCooldownError";
    this.remainingMs = remainingMs;
  }
  remainingMs: number;
}

function useRefreshAuthMutation(
  setTokenAction: ActionCreatorWithPayload<{ token: string }, string>,
) {
  const dispatch = useDispatch();

  return useMutation(
    ["refreshAuth"],
    async ({ token, userId }: { token: string; userId?: string }) => {
      const now = Date.now();
      const timeSinceLastRefresh = now - lastGlobalRefreshTime;

      // Check cooldown — throw so callers can schedule a retry
      if (timeSinceLastRefresh < REFRESH_COOLDOWN_MS) {
        const remainingMs = REFRESH_COOLDOWN_MS - timeSinceLastRefresh;
        throw new RefreshCooldownError(remainingMs);
      }

      // Update last refresh time
      lastGlobalRefreshTime = now;
      return refreshAuth({ token, userId });
    },
    {
      onSuccess: (data) => {
        const newToken = data?.accessToken;

        if (newToken) {
          dispatch(setTokenAction({ token: newToken }));
        } else {
          console.warn("No token received from refresh response");
        }
      },
      onError: (error) => {
        // Cooldown errors are expected and handled by the caller — don't log as failures
        if (error instanceof RefreshCooldownError) return;
        console.error("Token refresh failed:", error);
      },
    },
  );
}

export default useRefreshAuthMutation;
